import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  CreateProjectLocaleGlossaryInput,
  CreateProjectLocaleProfileInput,
  ProjectLocaleGlossary,
  ProjectLocaleProfile,
  UpdateProjectLocaleGlossaryInput,
  UpdateProjectLocaleProfileInput,
} from '../types/localeConfig.types';
import { getLocaleConfigService } from '../services/LocaleConfigService';

export interface UseLocaleAuthoringResult {
  profiles: ProjectLocaleProfile[];
  glossaries: ProjectLocaleGlossary[];
  loading: boolean;
  saving: boolean;
  error: Error | null;
  reload: () => Promise<void>;
  createProfile: (input: CreateProjectLocaleProfileInput) => Promise<void>;
  updateProfile: (profile: ProjectLocaleProfile, patch: UpdateProjectLocaleProfileInput) => Promise<void>;
  archiveProfile: (profile: ProjectLocaleProfile) => Promise<void>;
  createGlossary: (input: CreateProjectLocaleGlossaryInput) => Promise<void>;
  updateGlossary: (glossary: ProjectLocaleGlossary, patch: UpdateProjectLocaleGlossaryInput) => Promise<void>;
  archiveGlossary: (glossary: ProjectLocaleGlossary) => Promise<void>;
}

export function useLocaleAuthoring(projectId: string | undefined): UseLocaleAuthoringResult {
  const service = useMemo(() => getLocaleConfigService(), []);
  const [profiles, setProfiles] = useState<ProjectLocaleProfile[]>([]);
  const [glossaries, setGlossaries] = useState<ProjectLocaleGlossary[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const reload = useCallback(async () => {
    if (!projectId) {
      setProfiles([]);
      setGlossaries([]);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [nextProfiles, nextGlossaries] = await Promise.all([
        service.listProfiles(projectId),
        service.listGlossaries(projectId),
      ]);
      setProfiles(nextProfiles);
      setGlossaries(nextGlossaries);
      setError(null);
    } catch (cause) {
      setError(toError(cause, 'Failed to load localization profiles and glossaries.'));
    } finally {
      setLoading(false);
    }
  }, [projectId, service]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const mutate = useCallback(async (operation: () => Promise<unknown>) => {
    setSaving(true);
    try {
      await operation();
      await reload();
      setError(null);
    } catch (cause) {
      const nextError = toError(cause, 'Failed to save localization settings.');
      setError(nextError);
      throw nextError;
    } finally {
      setSaving(false);
    }
  }, [reload]);

  return {
    profiles,
    glossaries,
    loading,
    saving,
    error,
    reload,
    createProfile: async (input) => mutate(() => service.createProfile(requiredProjectId(projectId), input)),
    updateProfile: async (profile, patch) => mutate(() => service.updateProfile(
      requiredProjectId(projectId), profile.id, profile.version, patch,
    )),
    archiveProfile: async (profile) => mutate(() => service.archiveProfile(
      requiredProjectId(projectId), profile.id, profile.version,
    )),
    createGlossary: async (input) => mutate(() => service.createGlossary(requiredProjectId(projectId), input)),
    updateGlossary: async (glossary, patch) => mutate(() => service.updateGlossary(
      requiredProjectId(projectId), glossary.id, glossary.version, patch,
    )),
    archiveGlossary: async (glossary) => mutate(() => service.archiveGlossary(
      requiredProjectId(projectId), glossary.id, glossary.version,
    )),
  };
}

function requiredProjectId(projectId: string | undefined): string {
  if (!projectId) throw new Error('No project ID');
  return projectId;
}

function toError(value: unknown, fallback: string): Error {
  return value instanceof Error ? value : new Error(fallback);
}
